import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { resetPassword } from "../../lib/authApi";
import { ApiError } from "../../lib/api";
import Wordmark from "../../components/Brand/Wordmark";

type ResetState = "form" | "done" | "invalid";

/*
 * Velaigal /reset-password — the email-link target from the reset_password
 * send. A missing token can't succeed, so it starts straight in the
 * `invalid` state; a present-but-expired/used token only surfaces as
 * `invalid_token` once the form is submitted (there's no pre-check
 * endpoint), at which point we swap to the same invalid panel.
 */
const ResetPassword: React.FC = () => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") ?? "";

  const [state, setState] = useState<ResetState>(token ? "form" : "invalid");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await resetPassword(token, password);
      setState("done");
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_token") {
        setState("invalid");
      } else if (err instanceof ApiError && err.fields?.password) {
        setError(err.fields.password);
      } else {
        toast.error(err instanceof ApiError ? err.message : "Could not reset password.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-cream">
      {/* Left half — editorial framing, matches LoginPage's wrapper. */}
      <aside className="relative bg-cream px-8 py-12 lg:px-16 lg:py-20 flex flex-col justify-between">
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-8 right-10 select-none pointer-events-none"
          style={{
            fontFamily: '"Noto Serif Tamil", Georgia, serif',
            fontSize: "72px",
            fontWeight: 500,
            color: "var(--color-ink)",
            opacity: 0.18,
            lineHeight: 1,
          }}
        >
          வே
        </span>

        <Link
          to="/"
          aria-label="Velaigal home"
          className="text-ink no-underline self-start relative z-10"
          style={{ textDecoration: "none" }}
        >
          <Wordmark variant="lockup" size="md" animateOnMount />
        </Link>

        <div className="lg:max-w-md mt-12 lg:mt-0 relative z-10">
          <h1
            className="font-display text-3xl lg:text-5xl font-medium text-ink leading-tight tracking-tight velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Choose a new <span className="text-saffron">password</span>.
          </h1>
          <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
          <p className="mt-6 text-base lg:text-lg text-ink-secondary leading-relaxed">
            Make it something you will remember — at least 10 characters.
          </p>
        </div>

        <span className="hidden lg:block text-[11px] uppercase tracking-widest text-ink-tertiary mt-12">
          Made for hiring teams and jobseekers across the World.
        </span>
      </aside>

      {/* Right half — form / state panel. */}
      <section className="bg-paper flex items-center justify-center px-6 py-12 lg:px-16 lg:py-20 border-t lg:border-t-0 lg:border-l border-hairline">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 text-center">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary">
              Reset password
            </span>
          </div>

          {state === "form" && (
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-ink">
                  New password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  placeholder="At least 10 characters"
                  aria-invalid={!!error}
                  className="input"
                  style={error ? { borderColor: "var(--color-error)" } : undefined}
                />
                {error && (
                  <p role="alert" className="text-xs text-error font-medium">
                    {error}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="confirm" className="block text-sm font-medium text-ink">
                  Confirm password
                </label>
                <input
                  id="confirm"
                  name="confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat password"
                  className="input"
                />
              </div>

              <button type="submit" disabled={busy} className="btn-primary w-full">
                {busy ? "Resetting…" : "Reset password"}
              </button>
            </form>
          )}

          {state === "done" && (
            <div className="text-center space-y-6">
              <h2 className="font-display text-2xl font-medium text-ink tracking-tight">
                Password reset.
              </h2>
              <p className="text-sm text-ink-secondary leading-relaxed">
                Sign in with your new password.
              </p>
              <Link to="/login" className="btn-primary w-full">
                Sign in
              </Link>
            </div>
          )}

          {state === "invalid" && (
            <div className="text-center space-y-6">
              <h2 className="font-display text-2xl font-medium text-ink tracking-tight">
                Link invalid or expired.
              </h2>
              <p className="text-sm text-ink-secondary leading-relaxed">
                Reset links only work once and expire after an hour. Request
                a fresh one.
              </p>
              <Link to="/forgot-password" className="btn-primary w-full">
                Request a new link
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ResetPassword;
